import { celebrationDateOnly } from "@/lib/orderConfirmPrefill";
import type { RestoredInitialBrief } from "@/hooks/useInlineConversation";

/** Pinned celebration context for floating concierge checkout prefill. */
export type FloatingCelebrationContext = {
  recipientId: string;
  recipientName: string;
  occasion?: string;
  celebrationId?: string;
  celebrationDate?: string;
};

export type ResolvedBriefSnapshot = {
  recipient_id?: string | null;
  occasion?: string | null;
  celebration_id?: string | null;
  delivery_date?: string | null;
};

export function parseResolvedBriefSnapshot(
  value: unknown,
): ResolvedBriefSnapshot | null {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  const recipient_id =
    typeof row.recipient_id === "string" ? row.recipient_id : null;
  const occasion = typeof row.occasion === "string" ? row.occasion : null;
  const celebration_id =
    typeof row.celebration_id === "string" ? row.celebration_id : null;
  const delivery_date =
    typeof row.delivery_date === "string" ? row.delivery_date : null;
  if (!recipient_id && !occasion && !celebration_id) return null;
  return { recipient_id, occasion, celebration_id, delivery_date };
}

export interface FloatingCheckoutPrefill {
  recipientId: string | null;
  recipientName: string;
  occasion: string;
  celebrationId: string | null;
  defaultDate?: string;
}

/** Merge pinned context, resolved_brief, and team directory into checkout props. */
export function buildFloatingCheckoutPrefill(input: {
  pinned: FloatingCelebrationContext | null;
  resolvedBrief: unknown;
  restoredBrief: RestoredInitialBrief | null;
  teamMemberNameById: Map<string, string>;
  celebrationDateById: Map<string, string>;
}): FloatingCheckoutPrefill | null {
  const resolved = parseResolvedBriefSnapshot(input.resolvedBrief);
  const recipientId =
    input.pinned?.recipientId ?? resolved?.recipient_id ?? null;

  if (!recipientId) return null;

  const recipientName =
    input.pinned?.recipientName ??
    input.restoredBrief?.recipient_name ??
    input.teamMemberNameById.get(recipientId) ??
    "";

  const occasion =
    input.pinned?.occasion ??
    resolved?.occasion ??
    input.restoredBrief?.occasion ??
    "other";

  const celebrationId =
    input.pinned?.celebrationId ?? resolved?.celebration_id ?? null;

  const defaultDate =
    celebrationDateOnly(
      input.pinned?.celebrationDate ??
        resolved?.delivery_date ??
        (celebrationId ? input.celebrationDateById.get(celebrationId) : undefined),
    ) ?? undefined;

  return {
    recipientId,
    recipientName,
    occasion,
    celebrationId,
    defaultDate,
  };
}
